'use client'

import { useQueryClient } from '@tanstack/react-query'
import {
  getAuth,
  getIdToken,
  IdTokenResult,
  onAuthStateChanged,
  onIdTokenChanged,
  reload,
  User,
} from 'firebase/auth'
import { createContext, FC, PropsWithChildren, useEffect, useRef, useState } from 'react'

import { signOutAction } from '@/redux/actions'
import { useAppDispatch } from '@/redux/hooks'

export type FirebaseUserContextState = {
  isLoading: boolean
  reloadUser(): Promise<void>
} & (
  | {
      decodedJwt?: IdTokenResult
      firebaseUser: User
      isLoggedIn: true
    }
  | { decodedJwt: undefined; firebaseUser: null; isLoggedIn: false }
)

export const FirebaseUserContext = createContext<FirebaseUserContextState>({
  decodedJwt: undefined,
  firebaseUser: null,
  isLoading: true,
  isLoggedIn: false,
  reloadUser: async () => {},
})

type Props = PropsWithChildren

export const FirebaseUserContextProvider: FC<Props> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null)
  const [decodedJwt, setDecodedJwt] = useState<IdTokenResult>()
  const [isLoading, setIsLoading] = useState(true)
  const previousUser = useRef<User | null>(null)
  const previousFirebaseUser = useRef<User | null>(null)
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  useEffect(() => {
    const signedOut = previousFirebaseUser.current && !firebaseUser
    const signedIn = !previousFirebaseUser.current && firebaseUser

    if (signedIn) {
      console.log('signedIn')
      queryClient.removeQueries({
        queryKey: ['bankid-collect'],
      })
    }

    if (signedOut) {
      console.log('signedOut')
      dispatch(signOutAction())
      queryClient.removeQueries()
      setDecodedJwt(undefined)
      // ^ Issues with cookies in NextJS - disabled until we decide to fix it
      // setFirebaseCookie(undefined)
    }

    previousFirebaseUser.current = firebaseUser

    if (!firebaseUser) return

    let isMounted = true

    const updateIdToken = async () => {
      const { currentUser } = getAuth()

      if (!currentUser) return Promise.resolve(undefined)

      const decodedToken = await currentUser.getIdTokenResult()

      if (isMounted && decodedToken) {
        console.log('User was updated, setting new token')
        setDecodedJwt(decodedToken)
        // ^ Issues with cookies in NextJS - disabled until we decide to fix it
        // setFirebaseCookie(decodedToken.token, new Date(decodedToken.expirationTime))
      }
    }

    updateIdToken()

    return () => {
      isMounted = false
    }
  }, [firebaseUser])

  useEffect(() => {
    try {
      const auth = getAuth()

      const unsubAuthState = onAuthStateChanged(auth, user => {
        console.log('onAuthStateChanged()', user)
        previousUser.current = user
        setFirebaseUser(user)
        setIsLoading(false)
      })

      const unsubUserChanged = onIdTokenChanged(auth, async user => {
        console.log('onIdTokenChanged()', user)
        previousUser.current = user
        setFirebaseUser(user)
        setIsLoading(false)
      })

      console.log('Registered auth listeners')

      return () => {
        unsubAuthState()
        unsubUserChanged()
      }
    } catch (error) {
      console.error('Firebase initialization error:', error)
      setIsLoading(false)

      return
    }
  }, [])

  const reloadUser = async () => {
    let user = getAuth().currentUser
    if (!user) return
    await reload(user)
    await getIdToken(user, true)
    user = getAuth().currentUser ?? user
    // ^ Issues with cookies in NextJS - disabled until we decide to fix it
    // await setFirebaseCookie(token)
    previousUser.current = firebaseUser
    setFirebaseUser({ ...user })
  }

  const value: FirebaseUserContextState = firebaseUser
    ? {
        decodedJwt,
        firebaseUser,
        isLoading,
        isLoggedIn: true,
        reloadUser,
      }
    : {
        decodedJwt: undefined,
        firebaseUser: null,
        isLoading,
        isLoggedIn: false,
        reloadUser,
      }

  return <FirebaseUserContext.Provider value={value}>{children}</FirebaseUserContext.Provider>
}
